import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import Svg, { Circle, Line, Polygon, Text as SvgText } from 'react-native-svg';
import { Audio } from 'expo-av';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { GlassCard, Atmos } from '../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR, radii } from '../v10/theme';
import type { ToolProps } from '../olmps/ToolHost';
import { registerDeityForm, unregisterDeityForm } from '../olmps/deityFormRegistry';
import { PRESET_ENDEAVOURS } from '../olmps/question-banks';
import { loadOracleSpine, saveOracleSpine, type OracleSpine } from '../olmps/oracle-spine-data';

// ---------------------------------------------------------------------------
// Full-fidelity native TELOS. Mirrors, byte-for-byte where possible:
//   - src/app/telos/page.tsx            (compass page: reset/drift/cycle)
//   - src/app/telos/telos-utils.ts      (advanceCycleState, default state)
//   - src/app/telos/compass-geometry.ts (ring ratios, node placement)
//   - src/app/telos/life-compass.tsx    (static visual layout, sans sweep)
//   - src/app/telos/cascade-presets.ts  (preset visions/habits/principles)
//   - src/app/telos/cascade-table.tsx   (cascade editing: vision/habits/
//                                        actions/principles/notes/domains)
//   - src/app/journal/page.tsx          (markTelosDomain hit/miss buttons)
//   - src/interfaces/settings.ts        (TelosSettings/TelosDomainState
//                                        shapes + defaultAppSettings.telosSettings)
//
// The compass ring editing popovers from the web page are relocated into an
// expandable "Configure" section per domain card (same data, adapted layout
// for a phone-width panel) rather than tap-to-open floating popovers.
//
// Sweep line + Lyre audio (previously two declared exclusions, now closed):
//   - Sweep line mirrors life-compass.tsx's canvas draw (a radial line from
//     centre to R_OUTER+5 at `sweepAngle`, plus an end dot), driven by the
//     same rAF loop / timing as telos/page.tsx's handlePlayLyre (full 360°
//     sweep over `totalDuration` seconds, starting at -PI/2).
//   - Lyre audio: web (lyre.ts) uses Tone.js oscillators; expo-av can't
//     synthesize tones, so a tiny WAV synth below renders each domain's
//     3-note chord (summed sine waves + decay envelope) to a base64 PCM WAV
//     played via Audio.Sound.createAsync({ uri: 'data:audio/wav;base64,...' }).
//     Honors telosSettings.lyreEnabled. Chord notes/frequencies match web's
//     standard equal-temperament (A4=440) via noteToFrequency below. Also
//     detunes drifted domains sharp per web's lyre.ts driftToDetune formula.
// ---------------------------------------------------------------------------

type Pending = 'hit' | 'miss' | null;

interface TelosDomainCascade {
  selectedVisionId: string | null;
  customVisionText: string;
  selectedHabitIds: string[];
  customHabits: { id: string; name: string; tasks: string[] }[];
  selectedTaskIds: string[];
  customTaskTexts: string[];
  selectedPrincipleIds?: string[];
  customPrinciples?: string[];
  principleText?: string;
  notes?: string;
}

interface TelosDomainConfig {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
  visionText: string;
  systemText: string;
  taskText: string;
  chord: [string, string, string];
  cascade: TelosDomainCascade;
}

interface TelosSettings {
  domains: TelosDomainConfig[];
  lyreEnabled: boolean;
  driftRate: number;
  cycleDuration: '24hr' | '12hr' | 'weekly';
}

interface TelosDomainState {
  actionOffset: number;
  systemOffset: number;
  streak: number;
  missStreak: number;
  pending: Pending;
}

function PageBg() {
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Atmos />
    </View>
  );
}

function createDefaultCascade(): TelosDomainCascade {
  return {
    selectedVisionId: null,
    customVisionText: '',
    selectedHabitIds: [],
    customHabits: [],
    selectedTaskIds: [],
    customTaskTexts: [],
  };
}

function createDefaultDomainState(): TelosDomainState {
  return { actionOffset: 0, systemOffset: 0, streak: 0, missStreak: 0, pending: null };
}

// --- telos-utils.ts constants, verbatim ---
const DRIFT_PER_MISS = 0.06;
const RECOVERY_PER_HIT = 0.06;
const SYSTEM_LAG = 0.3;
const ENTROPY_TICK = 0.008;

function advanceCycleState(
  activeDomains: TelosDomainConfig[],
  currentStates: Record<string, TelosDomainState>
): Record<string, TelosDomainState> {
  const next = { ...currentStates };

  activeDomains.forEach((d) => {
    const st = { ...(next[d.id] || createDefaultDomainState()) };
    const action = st.pending || 'miss';

    if (action === 'hit') {
      st.actionOffset = Math.min(0, st.actionOffset + RECOVERY_PER_HIT);
      st.streak++;
      st.missStreak = 0;
    } else {
      st.actionOffset -= DRIFT_PER_MISS;
      st.missStreak++;
      st.streak = 0;
      st.actionOffset -= ENTROPY_TICK;
    }

    const diff = st.actionOffset - st.systemOffset;
    if (diff < 0) {
      if (st.missStreak >= 3) {
        st.systemOffset += diff * SYSTEM_LAG;
      } else if (st.missStreak >= 1) {
        st.systemOffset += diff * SYSTEM_LAG * 0.3;
      }
    } else {
      st.systemOffset += diff * SYSTEM_LAG * 0.5;
    }

    st.actionOffset = Math.max(-Math.PI * 0.4, st.actionOffset);
    st.systemOffset = Math.min(0, Math.max(st.systemOffset, -Math.PI * 0.4));
    if (Math.abs(st.actionOffset) < 0.001) st.actionOffset = 0;
    if (Math.abs(st.systemOffset) < 0.001) st.systemOffset = 0;

    st.pending = null;
    next[d.id] = st;
  });

  return next;
}

// --- compass-geometry.ts ratios, verbatim ---
const R_OUTER_RATIO = 0.44;
const R_MID_RATIO = 0.29;
const R_INNER_RATIO = 0.15;

function getPoint(cx: number, cy: number, angle: number, radius: number) {
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}

// ---------------------------------------------------------------------------
// Lyre audio (mobile exclusion, now closed). expo-av cannot synthesize tones
// the way web's Tone.js/Web Audio can, so we render a tiny mono 16-bit PCM
// WAV in-memory: a domain's 3-note chord as summed sine waves with a short
// attack + exponential-ish decay envelope, base64-encoded, then played via
// Audio.Sound with a data URI. Self-contained — no new deps. Also ports web's
// lyre.ts drift-based detune: a domain's action/system notes are pitched
// sharp proportionally to the domain's actionOffset drift (vision stays clean).
// ---------------------------------------------------------------------------
const LYRE_SAMPLE_RATE = 22050;
const LYRE_DURATION_SEC = 0.8;

/** Standard equal-temperament note→frequency (A4 = 440Hz), e.g. 'C4', 'F#4'. */
function noteToFrequency(note: string): number {
  const match = /^([A-G])(#|b)?(-?\d+)$/.exec(note.trim());
  if (!match) return 440;
  const [, letter, accidental, octaveStr] = match;
  const SEMITONES_FROM_C: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let semitone = SEMITONES_FROM_C[letter] ?? 0;
  if (accidental === '#') semitone += 1;
  else if (accidental === 'b') semitone -= 1;
  const octave = parseInt(octaveStr, 10);
  const midi = (octave + 1) * 12 + semitone;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Base64-encode a byte buffer without relying on `btoa` (RN has no DOM). */
function bytesToBase64(bytes: Uint8Array): string {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += CHARS[b0 >> 2];
    out += CHARS[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? CHARS[((b1 & 0x0f) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? CHARS[b2 & 0x3f] : '=';
  }
  return out;
}

/**
 * Cents-of-detune for a given drift (radians), matching web's lyre.ts driftToDetune:
 * scales linearly to 80 cents at the max drift clamp of 0.4 rad.
 */
function driftToDetune(driftRad: number): number {
  return (Math.abs(driftRad) / 0.4) * 80;
}

/** Applies a cents offset to a frequency: freq * 2^(cents/1200), matching web's noteFreqDetuned. */
function detuneFrequency(freq: number, centsDrift: number): number {
  return freq * Math.pow(2, centsDrift / 1200);
}

/**
 * Renders a 3-note chord (sum of sine waves, decay envelope) to a base64 WAV data URI.
 * `drift` is the domain's actionOffset (radians); action detunes fully, system detunes at
 * half that drift, vision stays clean — mirrors web's triggerSectorChord in lyre.ts.
 */
function synthesizeChordWav(chord: [string, string, string], drift = 0): string {
  const numSamples = Math.floor(LYRE_SAMPLE_RATE * LYRE_DURATION_SEC);
  const actionDetune = driftToDetune(drift);
  const systemDetune = driftToDetune(drift * 0.5);
  const freqs: number[] = [
    detuneFrequency(noteToFrequency(chord[0]), actionDetune), // action (most detuned)
    detuneFrequency(noteToFrequency(chord[1]), systemDetune), // system (slightly detuned)
    noteToFrequency(chord[2]), // vision (always clean)
  ];
  const attackSamples = Math.floor(LYRE_SAMPLE_RATE * 0.01);

  const pcm = new Int16Array(numSamples);
  for (let i = 0; i < numSamples; i++) {
    const t = i / LYRE_SAMPLE_RATE;
    let sample = 0;
    for (const f of freqs) sample += Math.sin(2 * Math.PI * f * t);
    sample /= freqs.length;

    const envelope =
      i < attackSamples ? i / attackSamples : Math.exp(-3 * (t - attackSamples / LYRE_SAMPLE_RATE));
    sample *= envelope * 0.6;

    pcm[i] = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
  }

  const blockAlign = 2; // mono, 16-bit
  const byteRate = LYRE_SAMPLE_RATE * blockAlign;
  const dataSize = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, LYRE_SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < pcm.length; i++) view.setInt16(44 + i * 2, pcm[i], true);

  const b64 = bytesToBase64(new Uint8Array(buffer));
  return `data:audio/wav;base64,${b64}`;
}

/** Plays a domain's 3-note chord once, cleaning the sound up when it finishes. */
async function playChord(chord: [string, string, string], drift = 0): Promise<void> {
  const uri = synthesizeChordWav(chord, drift);
  const { sound } = await Audio.Sound.createAsync({ uri }, { shouldPlay: true });
  sound.setOnPlaybackStatusUpdate((status) => {
    if (status.isLoaded && status.didJustFinish) {
      sound.unloadAsync().catch(() => {});
    }
  });
}

// --- Sector mapping for PRESET_ENDEAVOURS lookup ---
function domainIdToSector(domainId: string): 'S' | 'H' | 'A' | 'P' | 'E' | null {
  const sectorMap: Record<string, 'S' | 'H' | 'A' | 'P' | 'E'> = {
    'telos-social': 'S',
    'telos-health': 'H',
    'telos-admin': 'A',
    'telos-personal': 'P',
    'telos-economic': 'E',
  };
  return sectorMap[domainId] ?? null;
}

// --- cascade-presets.ts, verbatim domain data ---
interface TelosPresetTask {
  id: string;
  text: string;
}
interface TelosPresetHabit {
  id: string;
  name: string;
  tasks: TelosPresetTask[];
  principle: string;
}
interface TelosPresetVision {
  id: string;
  text: string;
}
interface TelosPresetPrinciple {
  id: string;
  text: string;
  habitId?: string;
}
interface TelosDomainPreset {
  domainId: string;
  visions: TelosPresetVision[];
  habits: TelosPresetHabit[];
  principles: TelosPresetPrinciple[];
}

function findPresetForDomain(domain: { id: string; name: string }): TelosDomainPreset | undefined {
  return (
    TELOS_CASCADE_PRESETS.find((p) => p.domainId === domain.id) ||
    TELOS_CASCADE_PRESETS.find((p) => {
      const presetName = p.domainId.replace('telos-', '').toLowerCase();
      return domain.name.toLowerCase() === presetName;
    })
  );
}

const TELOS_CASCADE_PRESETS: TelosDomainPreset[] = [
  // 1. SOCIAL
  {
    domainId: 'telos-social',
    visions: [
      { id: 'social-v1', text: 'Having relationships that make everyone involved stronger.' },
      { id: 'social-v2', text: 'Having a reputation that precedes and outlasts me.' },
      { id: 'social-v3', text: 'Having a circle rooted in mutual truth and mutual value.' },
      { id: 'social-v4', text: 'Having relationships that survive the truth.' },
      { id: 'social-v5', text: 'Having people who know me and choose to stay.' },
    ],
    habits: [
      {
        id: 'social-a', name: 'I invest in my close relationships',
        principle: 'Proximity is not loyalty.',
        tasks: [
          { id: 'social-a-1', text: 'Reached out to someone I care about' },
          { id: 'social-a-2', text: 'Gave someone my full presence (not just my time)' },
          { id: 'social-a-3', text: 'Did something for someone with no expectation of return' },
        ],
      },
      {
        id: 'social-b', name: 'I communicate with honesty',
        principle: 'The strongest bonds survive honest conflict.',
        tasks: [
          { id: 'social-b-1', text: 'Said something that needed to be said' },
          { id: 'social-b-2', text: 'Asked a real question instead of a polite one' },
          { id: 'social-b-3', text: 'Listened fully before responding' },
        ],
      },
      {
        id: 'social-c', name: 'I manage my reputation intentionally',
        principle: 'Reputation is built in private and revealed in public.',
        tasks: [
          { id: 'social-c-1', text: 'Followed through on something I committed to' },
          { id: 'social-c-2', text: 'Showed up the way I want to be known' },
          { id: 'social-c-3', text: 'Addressed a misunderstanding or unresolved tension' },
        ],
      },
      {
        id: 'social-d', name: 'I expand my network with purpose',
        principle: 'Every relationship is an exchange — the question is whether both sides know the terms.',
        tasks: [
          { id: 'social-d-1', text: 'Connected with someone new who aligns with my direction' },
          { id: 'social-d-2', text: 'Attended or participated in a community or group' },
          { id: 'social-d-3', text: 'Offered value to someone before asking for anything' },
        ],
      },
      {
        id: 'social-e', name: 'I set and maintain boundaries',
        principle: 'You teach people how to treat you by what you tolerate.',
        tasks: [
          { id: 'social-e-1', text: 'Said no to something that didn\'t serve me or the relationship' },
          { id: 'social-e-2', text: 'Enforced a boundary I\'ve already communicated' },
          { id: 'social-e-3', text: 'Removed myself from a draining interaction' },
        ],
      },
    ],
    principles: [
      { id: 'social-p1', text: 'Every relationship is an exchange — the question is whether both sides know the terms.' },
      { id: 'social-p2', text: 'Proximity is not loyalty.' },
      { id: 'social-p3', text: 'You teach people how to treat you by what you tolerate.' },
      { id: 'social-p4', text: 'Reputation is built in private and revealed in public.' },
    ],
  },

  // 2. HEALTH
  {
    domainId: 'telos-health',
    visions: [
      { id: 'health-v1', text: 'Having a body that meets every demand I place on it.' },
      { id: 'health-v2', text: 'Having physical capacity that never limits my life.' },
      { id: 'health-v3', text: 'Having a body that performs when called upon.' },
      { id: 'health-v4', text: 'Having a machine I maintain with the respect it deserves.' },
      { id: 'health-v5', text: 'Having energy that is earned, never borrowed.' },
    ],
    habits: [
      {
        id: 'health-a', name: 'I move consistently',
        principle: 'The body adapts to what you consistently demand of it.',
        tasks: [
          { id: 'health-a-1', text: '30 min intentional movement' },
          { id: 'health-a-2', text: 'Took the harder physical option (stairs, walked, carried)' },
          { id: 'health-a-3', text: 'Stretched or mobilized for 10 min' },
        ],
      },
      {
        id: 'health-b', name: 'I eat with purpose',
        principle: 'You are not what you eat — you are what you consistently eat.',
        tasks: [
          { id: 'health-b-1', text: 'Prepped or cooked at least one meal' },
          { id: 'health-b-2', text: 'Drank water first thing in the morning' },
          { id: 'health-b-3', text: 'Ate mostly whole foods today' },
        ],
      },
      {
        id: 'health-c', name: 'I protect my rest',
        principle: 'Rest is a strategy, not a reward.',
        tasks: [
          { id: 'health-c-1', text: 'In bed by target time' },
          { id: 'health-c-2', text: 'Screens off 30 min before sleep' },
          { id: 'health-c-3', text: 'Got 7+ hours' },
        ],
      },
      {
        id: 'health-d', name: 'I stay on top of my health',
        principle: 'Neglect compounds faster than effort does.',
        tasks: [
          { id: 'health-d-1', text: 'Took vitamins/meds on schedule' },
          { id: 'health-d-2', text: 'Addressed something I\'ve been putting off (appointment, checkup, etc.)' },
          { id: 'health-d-3', text: 'Checked in with how my body feels today' },
        ],
      },
      {
        id: 'health-e', name: 'I keep myself clean and maintained',
        principle: 'How you maintain the outside reflects how you regard the inside.',
        tasks: [
          { id: 'health-e-1', text: 'Full hygiene routine completed' },
          { id: 'health-e-2', text: 'Grooming and appearance handled' },
          { id: 'health-e-3', text: 'Living space is clean and organized' },
        ],
      },
    ],
    principles: [
      { id: 'health-p1', text: 'Energy is earned through discipline, not motivation.' },
      { id: 'health-p2', text: 'The body adapts to what you consistently demand of it.' },
      { id: 'health-p3', text: 'Neglect compounds faster than effort does.' },
      { id: 'health-p4', text: 'Comfort is the enemy of capability.' },
    ],
  },

  // 3. ADMIN
  {
    domainId: 'telos-admin',
    visions: [
      { id: 'admin-v1', text: 'Having a life administered with the same care I\'d give a business.' },
      { id: 'admin-v2', text: 'Having no dropped balls — nothing forgotten, nothing overdue.' },
      { id: 'admin-v3', text: 'Having systems that run themselves once built.' },
      { id: 'admin-v4', text: 'Having a home and paperwork trail that could survive an audit.' },
      { id: 'admin-v5', text: 'Having control over every obligation, not the other way around.' },
    ],
    habits: [
      {
        id: 'admin-a', name: 'I process my inbox and paperwork',
        principle: 'An unopened envelope is a decision deferred, not avoided.',
        tasks: [
          { id: 'admin-a-1', text: 'Cleared email/messages inbox to zero or near-zero' },
          { id: 'admin-a-2', text: 'Opened and processed physical mail or paperwork' },
          { id: 'admin-a-3', text: 'Filed or archived a document that needed it' },
        ],
      },
      {
        id: 'admin-b', name: 'I maintain my calendar and commitments',
        principle: 'A calendar you don\'t trust is worse than no calendar at all.',
        tasks: [
          { id: 'admin-b-1', text: 'Checked tomorrow\'s schedule before bed' },
          { id: 'admin-b-2', text: 'Confirmed or rescheduled an appointment' },
          { id: 'admin-b-3', text: 'Added a forgotten commitment before it became a crisis' },
        ],
      },
      {
        id: 'admin-c', name: 'I keep my systems running',
        principle: 'Systems decay unless someone maintains them.',
        tasks: [
          { id: 'admin-c-1', text: 'Updated a recurring system, checklist, or template' },
          { id: 'admin-c-2', text: 'Automated or simplified a repeated task' },
          { id: 'admin-c-3', text: 'Backed up or organized digital files' },
        ],
      },
      {
        id: 'admin-d', name: 'I stay on top of my obligations',
        principle: 'The obligations you ignore compound into obligations you can\'t ignore.',
        tasks: [
          { id: 'admin-d-1', text: 'Paid or scheduled a bill or renewal before it was late' },
          { id: 'admin-d-2', text: 'Renewed or checked on a license, subscription, or policy' },
          { id: 'admin-d-3', text: 'Responded to a pending request or form' },
        ],
      },
      {
        id: 'admin-e', name: 'I maintain my home and belongings',
        principle: 'A disordered space is administration deferred.',
        tasks: [
          { id: 'admin-e-1', text: 'Did a load of laundry or dishes' },
          { id: 'admin-e-2', text: 'Tidied or reset a space to baseline' },
          { id: 'admin-e-3', text: 'Fixed or scheduled a repair for something broken' },
        ],
      },
    ],
    principles: [
      { id: 'admin-p1', text: 'An unopened envelope is a decision deferred, not avoided.' },
      { id: 'admin-p2', text: 'Systems decay unless someone maintains them.' },
      { id: 'admin-p3', text: 'The obligations you ignore compound into obligations you can\'t ignore.' },
      { id: 'admin-p4', text: 'A calendar you don\'t trust is worse than no calendar at all.' },
    ],
  },

  // 4. PERSONAL
  {
    domainId: 'telos-personal',
    visions: [
      { id: 'personal-v1', text: 'Having a mind that operates with clarity and intention in every situation.' },
      { id: 'personal-v2', text: 'Having a character that requires no explanation.' },
      { id: 'personal-v3', text: 'Having an inner life that drives everything else forward.' },
      { id: 'personal-v4', text: 'Having complete ownership of who I am and who I\'m becoming.' },
      { id: 'personal-v5', text: 'Having a self I built on purpose, not by accident.' },
    ],
    habits: [
      {
        id: 'personal-a', name: 'I learn consistently',
        principle: 'Growth requires honest discomfort, not positive thinking.',
        tasks: [
          { id: 'personal-a-1', text: 'Read or studied for at least 20 min' },
          { id: 'personal-a-2', text: 'Practiced a skill I\'m developing' },
          { id: 'personal-a-3', text: 'Applied something new I recently learned' },
        ],
      },
      {
        id: 'personal-b', name: 'I reflect and journal',
        principle: 'An unexamined pattern will repeat until it\'s named.',
        tasks: [
          { id: 'personal-b-1', text: 'Wrote in my journal today' },
          { id: 'personal-b-2', text: 'Reviewed yesterday\'s decisions honestly' },
          { id: 'personal-b-3', text: 'Identified one pattern I want to change or reinforce' },
        ],
      },
      {
        id: 'personal-c', name: 'I manage my emotional state',
        principle: 'You can only manage what you\'re willing to see clearly.',
        tasks: [
          { id: 'personal-c-1', text: 'Paused before reacting to something that triggered me' },
          { id: 'personal-c-2', text: 'Named an emotion instead of acting on it blindly' },
          { id: 'personal-c-3', text: 'Chose a response instead of defaulting to a reaction' },
        ],
      },
      {
        id: 'personal-d', name: 'I create consistently',
        principle: 'Identity is built by what you repeatedly do, not what you believe about yourself.',
        tasks: [
          { id: 'personal-d-1', text: 'Made something today (wrote, built, designed, composed)' },
          { id: 'personal-d-2', text: 'Worked on my passion project' },
          { id: 'personal-d-3', text: 'Spent time in creative flow' },
        ],
      },
      {
        id: 'personal-e', name: 'I stay spiritually/mentally grounded',
        principle: 'What you avoid defines you more than what you pursue.',
        tasks: [
          { id: 'personal-e-1', text: 'Meditated, prayed, or sat in silence' },
          { id: 'personal-e-2', text: 'Spent time in nature or away from noise' },
          { id: 'personal-e-3', text: 'Practiced gratitude intentionally' },
        ],
      },
    ],
    principles: [
      { id: 'personal-p1', text: 'Identity is built by what you repeatedly do, not what you believe about yourself.' },
      { id: 'personal-p2', text: 'Growth requires honest discomfort, not positive thinking.' },
      { id: 'personal-p3', text: 'What you avoid defines you more than what you pursue.' },
      { id: 'personal-p4', text: 'An unexamined pattern will repeat until it\'s named.' },
    ],
  },

  // 5. ECONOMIC
  {
    domainId: 'telos-economic',
    visions: [
      { id: 'economic-v1', text: 'Having systems that generate freedom and eliminate dependency.' },
      { id: 'economic-v2', text: 'Having a foundation that works independently of my time.' },
      { id: 'economic-v3', text: 'Having infrastructure that compounds whether I\'m present or not.' },
      { id: 'economic-v4', text: 'Having more options every year and fewer dependencies.' },
      { id: 'economic-v5', text: 'Having wealth that reflects the value I create.' },
    ],
    habits: [
      {
        id: 'economic-a', name: 'I manage my money actively',
        principle: 'What you don\'t track, you lose.',
        tasks: [
          { id: 'economic-a-1', text: 'Reviewed my spending today' },
          { id: 'economic-a-2', text: 'Stayed within my daily/weekly budget' },
          { id: 'economic-a-3', text: 'Put something into savings (any amount)' },
        ],
      },
      {
        id: 'economic-b', name: 'I protect what I\'ve built',
        principle: 'Ownership without maintenance is just delayed loss.',
        tasks: [
          { id: 'economic-b-1', text: 'Checked on an account, investment, or policy that matters' },
          { id: 'economic-b-2', text: 'Reviewed a statement or balance for errors' },
          { id: 'economic-b-3', text: 'Addressed a financial loose end' },
        ],
      },
      {
        id: 'economic-c', name: 'I invest in my future',
        principle: 'Systems produce wealth; effort alone produces income.',
        tasks: [
          { id: 'economic-c-1', text: 'Contributed to a long-term goal (retirement, education, asset)' },
          { id: 'economic-c-2', text: 'Learned something about managing money better' },
          { id: 'economic-c-3', text: 'Made a decision today that future me will thank me for' },
        ],
      },
      {
        id: 'economic-d', name: 'I build my career and earning capacity',
        principle: 'Every resource is either growing or shrinking — nothing holds.',
        tasks: [
          { id: 'economic-d-1', text: 'Worked on a project or deliverable that advances my position' },
          { id: 'economic-d-2', text: 'Invested time in a skill with professional return' },
          { id: 'economic-d-3', text: 'Explored a new opportunity or income stream' },
        ],
      },
      {
        id: 'economic-e', name: 'I create and protect income streams',
        principle: 'One income stream is one point of failure.',
        tasks: [
          { id: 'economic-e-1', text: 'Worked on a side project, asset, or income stream outside my main job' },
          { id: 'economic-e-2', text: 'Reviewed or optimized an existing income source' },
          { id: 'economic-e-3', text: 'Took a step toward diversifying how I earn' },
        ],
      },
    ],
    principles: [
      { id: 'economic-p1', text: 'Every resource is either growing or shrinking — nothing holds.' },
      { id: 'economic-p2', text: 'What you don\'t track, you lose.' },
      { id: 'economic-p3', text: 'Systems produce wealth; effort alone produces income.' },
      { id: 'economic-p4', text: 'Ownership without maintenance is just delayed loss.' },
    ],
  },
];

// --- settings-context.tsx defaultAppSettings.telosSettings, verbatim ---
const DEFAULT_TELOS_SETTINGS: TelosSettings = {
  domains: [
    {
      id: 'telos-social',
      name: 'Social',
      chord: ['F4', 'A4', 'C5'],
      color: SECTOR_COLOR.S,
      cascade: createDefaultCascade(),
      isActive: true,
      taskText: '',
      systemText: '',
      visionText: '',
    },
    {
      id: 'telos-health',
      name: 'Health',
      chord: ['C4', 'E4', 'G4'],
      color: SECTOR_COLOR.H,
      cascade: {
        customHabits: [],
        customTaskTexts: [],
        selectedTaskIds: [],
        customPrinciples: [
          'Order is Life',
          'Order over Action, Always',
          "If you don't know what to do, it's SyQi Order",
        ],
        customVisionText: '',
        selectedHabitIds: ['health-b'],
        selectedVisionId: 'health-v2',
      },
      isActive: true,
      taskText: '',
      systemText: '',
      visionText: '',
    },
    {
      id: 'telos-admin',
      name: 'Admin',
      chord: ['D4', 'F#4', 'A4'],
      color: SECTOR_COLOR.A,
      cascade: createDefaultCascade(),
      isActive: true,
      taskText: '',
      systemText: '',
      visionText: '',
    },
    {
      id: 'telos-personal',
      name: 'Personal',
      chord: ['G4', 'B4', 'D5'],
      color: SECTOR_COLOR.P,
      cascade: createDefaultCascade(),
      isActive: true,
      taskText: '',
      systemText: '',
      visionText: '',
    },
    {
      id: 'telos-economic',
      name: 'Economic',
      chord: ['E4', 'G#4', 'B4'],
      color: SECTOR_COLOR.E,
      cascade: createDefaultCascade(),
      isActive: true,
      taskText: '',
      systemText: '',
      visionText: '',
    },
  ],
  driftRate: 2,
  lyreEnabled: true,
  cycleDuration: '24hr',
};

// --- cascade-table.tsx text-resolution logic, replicated for read/edit ---
function resolveVisionText(domain: TelosDomainConfig): string {
  const cascade = domain.cascade;
  if (!cascade) return domain.visionText || '';
  if (cascade.selectedVisionId === '__custom__') return cascade.customVisionText || domain.visionText || '';
  // selectedVisionId now matches a PRESET_ENDEAVOURS string directly (V9 spec §13 #6)
  return cascade.selectedVisionId || domain.visionText || '';
}

function resolveHabitNames(domain: TelosDomainConfig): string[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedIds = cascade?.selectedHabitIds || [];
  const presetNames = (preset?.habits || []).filter((h) => selectedIds.includes(h.id)).map((h) => h.name);
  const customNames = (cascade?.customHabits || []).map((h) => h.name);
  return [...presetNames, ...customNames];
}

function availableTasksForDomain(domain: TelosDomainConfig): { id: string; text: string }[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedHabitIds = cascade?.selectedHabitIds || [];
  const presetTasks = (preset?.habits || []).filter((h) => selectedHabitIds.includes(h.id)).flatMap((h) => h.tasks);
  const customHabitTasks = (cascade?.customHabits || []).flatMap((h) =>
    h.tasks.map((t, i) => ({ id: `${h.id}-task-${i}`, text: t }))
  );
  return [...presetTasks, ...customHabitTasks];
}

function resolveActionTexts(domain: TelosDomainConfig): string[] {
  const { t } = useTheme();
  const cascade = domain.cascade;
  const selectedTaskIds = cascade?.selectedTaskIds || [];
  const allTasks = availableTasksForDomain(domain);
  const selected = allTasks.filter((t) => selectedTaskIds.includes(t.id)).map((t) => t.text);
  return [...selected, ...(cascade?.customTaskTexts || [])];
}

function availablePrinciplesForDomain(domain: TelosDomainConfig): { id: string; text: string }[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedHabitIds = cascade?.selectedHabitIds || [];
  const habitPrinciples = (preset?.habits || [])
    .filter((h) => selectedHabitIds.includes(h.id))
    .map((h) => ({ id: `${h.id}-principle`, text: h.principle }));
  const generalPrinciples = preset?.principles || [];
  return [...habitPrinciples, ...generalPrinciples];
}

function resolvePrincipleTexts(domain: TelosDomainConfig): string[] {
  const cascade = domain.cascade;
  const selectedPrincipleIds = cascade?.selectedPrincipleIds || [];
  const allPrinciples = availablePrinciplesForDomain(domain);
  const selected = allPrinciples.filter((p) => selectedPrincipleIds.includes(p.id)).map((p) => p.text);
  return [...selected, ...(cascade?.customPrinciples || [])];
}

function degrees(radians: number): string {
  return Math.abs((radians || 0) * 180 / Math.PI).toFixed(1);
}

// ---------------------------------------------------------------------------
// Static compass (react-native-svg). Mirrors life-compass.tsx's canvas draw,
// minus the animated sweep line (allowed exclusion).
// ---------------------------------------------------------------------------
function TelosCompass({
  domains,
  domainStates,
  size,
  sweepAngle = null,
}: {
  domains: TelosDomainConfig[];
  domainStates: Record<string, TelosDomainState>;
  size: number;
  sweepAngle?: number | null;
}) {
  const { t, c } = useTheme();
  const activeDomains = domains.filter((d) => d.isActive);
  const n = activeDomains.length;
  if (n === 0) return null;

  const CX = size / 2;
  const CY = size / 2;
  const R_OUTER = size * R_OUTER_RATIO;
  const R_MID = size * R_MID_RATIO;
  const R_INNER = size * R_INNER_RATIO;

  const geo = activeDomains.map((d, i) => {
    const baseAngle = -Math.PI / 2 + (2 * Math.PI / n) * i;
    const st = domainStates[d.id];
    const actionOffset = st?.actionOffset || 0;
    const systemOffset = st?.systemOffset || 0;
    const visionPt = getPoint(CX, CY, baseAngle, R_OUTER);
    const systemPt = getPoint(CX, CY, baseAngle + systemOffset, R_MID);
    const actionPt = getPoint(CX, CY, baseAngle + actionOffset, R_INNER);
    const idealInner = getPoint(CX, CY, baseAngle, R_INNER);
    const idealMid = getPoint(CX, CY, baseAngle, R_MID);
    const labelPt = getPoint(CX, CY, baseAngle, R_OUTER + 16);
    return { domain: d, visionPt, systemPt, actionPt, idealInner, idealMid, labelPt };
  });

  const idealPolygonPoints = geo.map((g) => `${g.idealInner.x},${g.idealInner.y}`).join(' ');
  const actualPolygonPoints = geo.map((g) => `${g.actionPt.x},${g.actionPt.y}`).join(' ');

  return (
    <Svg width={size} height={size}>
      <Circle cx={CX} cy={CY} r={R_OUTER} stroke={t.lineFaint} strokeWidth={1.5} fill="none" />
      <Circle cx={CX} cy={CY} r={R_MID} stroke={t.lineFaint} strokeWidth={1.5} fill="none" />
      <Circle cx={CX} cy={CY} r={R_INNER} stroke={c.textFaint} strokeWidth={1} fill="none" />

      <Polygon points={idealPolygonPoints} stroke={c.card} strokeWidth={1} strokeDasharray="4,4" fill="none" />
      <Polygon points={actualPolygonPoints} stroke={c.textFaint} strokeWidth={1.5} fill={t.goldGlow.replace(/[\d.]+\)$/, '0.12)')} />

      {geo.map((g) => (
        <React.Fragment key={g.domain.id}>
          <Line x1={g.idealInner.x} y1={g.idealInner.y} x2={g.idealMid.x} y2={g.idealMid.y} stroke={g.domain.color} strokeWidth={1} strokeOpacity={0.08} />
          <Line x1={g.idealMid.x} y1={g.idealMid.y} x2={g.visionPt.x} y2={g.visionPt.y} stroke={g.domain.color} strokeWidth={1} strokeOpacity={0.08} />

          <Line x1={g.actionPt.x} y1={g.actionPt.y} x2={g.systemPt.x} y2={g.systemPt.y} stroke={g.domain.color} strokeWidth={2} strokeOpacity={0.35} />
          <Line x1={g.systemPt.x} y1={g.systemPt.y} x2={g.visionPt.x} y2={g.visionPt.y} stroke={g.domain.color} strokeWidth={2} strokeOpacity={0.35} />

          <Circle cx={g.visionPt.x} cy={g.visionPt.y} r={7} fill={g.domain.color} fillOpacity={0.4} />
          <Circle cx={g.systemPt.x} cy={g.systemPt.y} r={5.5} fill={g.domain.color} fillOpacity={0.7} />
          <Circle cx={g.actionPt.x} cy={g.actionPt.y} r={5.5} fill={g.domain.color} />

          <SvgText x={g.labelPt.x} y={g.labelPt.y} fontSize={10} fontWeight="700" fill={g.domain.color} textAnchor="middle">
            {g.domain.name.toUpperCase()}
          </SvgText>
        </React.Fragment>
      ))}

      <SvgText x={CX} y={CY - 3} fontSize={12} fontWeight="900" fill={t.ivoryFaint} textAnchor="middle">
        YOU
      </SvgText>
      <SvgText x={CX} y={CY + 10} fontSize={7} fontWeight="500" fill={t.ivoryFaint} textAnchor="middle">
        ARE HERE
      </SvgText>

      <SvgText x={CX - R_INNER - 4} y={CY - 2} fontSize={8} fill={c.baseDark} textAnchor="end">
        ACTIONS
      </SvgText>
      <SvgText x={CX - R_MID - 4} y={CY - 2} fontSize={8} fill={c.baseDark} textAnchor="end">
        SYSTEMS
      </SvgText>
      <SvgText x={CX - R_OUTER - 4} y={CY - 2} fontSize={8} fill={c.baseDark} textAnchor="end">
        VISION
      </SvgText>

      {sweepAngle !== null ? (
        (() => {
          const sweepEnd = getPoint(CX, CY, sweepAngle, R_OUTER + 5);
          return (
            <React.Fragment>
              <Line x1={CX} y1={CY} x2={sweepEnd.x} y2={sweepEnd.y} stroke={t.navyLine} strokeWidth={1.5} />
              <Circle cx={sweepEnd.x} cy={sweepEnd.y} r={3} fill={t.goldGlow} />
            </React.Fragment>
          );
        })()
      ) : null}
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// Small building blocks for the cascade editor (mirrors cascade-table.tsx).
// ---------------------------------------------------------------------------
function Chip({ label, selected, color, onPress }: { label: string; selected: boolean; color?: string; onPress: () => void }) {
  const styles = useStyles();
  const { c } = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.chip,
        selected && { backgroundColor: color || c.blue, borderColor: color || c.blue },
      ]}
    >
      <Text style={[styles.chipText, selected && styles.chipTextSelected]} numberOfLines={2}>
        {label}
      </Text>
    </Pressable>
  );
}

function AddInlineField({ placeholder, onSubmit }: { placeholder: string; onSubmit: (text: string) => void }) {
  const styles = useStyles();
  const { c } = useTheme();
  const [value, setValue] = useState('');
  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue('');
  };
  return (
    <View style={styles.addRow}>
      <TextInput
        style={styles.addInput}
        placeholder={placeholder}
        placeholderTextColor={c.slate}
        value={value}
        onChangeText={setValue}
        onSubmitEditing={commit}
        returnKeyType="done"
      />
      <Pressable style={styles.addBtn} onPress={commit}>
        <Text style={styles.addBtnText}>+</Text>
      </Pressable>
    </View>
  );
}

interface DomainCardProps {
  domain: TelosDomainConfig;
  state: TelosDomainState;
  onMark: (action: 'hit' | 'miss') => void;
  onRename: (name: string) => void;
  onRemove: () => void;
  onCascadeChange: (update: Partial<TelosDomainCascade>) => void;
}

function DomainCard({ domain, state, onMark, onRename, onRemove, onCascadeChange }: DomainCardProps) {
  const styles = useStyles();
  const { t, c } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(domain.name);
  const [customVisionDraft, setCustomVisionDraft] = useState(domain.cascade?.customVisionText || '');
  const [notesDraft, setNotesDraft] = useState(domain.cascade?.notes || '');

  const cascade = domain.cascade || createDefaultCascade();
  const preset = findPresetForDomain(domain);

  const isAligned = state.actionOffset === 0 && state.systemOffset === 0;
  const actionDeg = degrees(state.actionOffset);
  const sysDeg = degrees(state.systemOffset);

  const visionText = resolveVisionText(domain);
  const habitNames = resolveHabitNames(domain);
  const actionTexts = resolveActionTexts(domain);
  const principleTexts = resolvePrincipleTexts(domain);
  const availableTasks = availableTasksForDomain(domain);
  const availablePrinciples = availablePrinciplesForDomain(domain);

  const commitName = () => {
    setEditingName(false);
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== domain.name) onRename(trimmed);
    else setNameDraft(domain.name);
  };

  const toggleHabit = (habitId: string) => {
    const ids = cascade.selectedHabitIds || [];
    onCascadeChange({
      selectedHabitIds: ids.includes(habitId) ? ids.filter((id) => id !== habitId) : [...ids, habitId],
    });
  };

  const toggleTask = (taskId: string) => {
    const ids = cascade.selectedTaskIds || [];
    onCascadeChange({
      selectedTaskIds: ids.includes(taskId) ? ids.filter((id) => id !== taskId) : [...ids, taskId],
    });
  };

  const togglePrinciple = (principleId: string) => {
    const ids = cascade.selectedPrincipleIds || [];
    onCascadeChange({
      selectedPrincipleIds: ids.includes(principleId) ? ids.filter((id) => id !== principleId) : [...ids, principleId],
    });
  };

  const confirmRemove = () => {
    Alert.alert('Delete this domain?', 'This removes the domain and its cascade configuration.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: onRemove },
    ]);
  };

  return (
    <GlassCard style={[styles.card, { borderColor: domain.color }]}>
      <View style={styles.cardHeaderRow}>
        {editingName ? (
          <TextInput
            style={[styles.nameInput, { color: domain.color }]}
            value={nameDraft}
            onChangeText={setNameDraft}
            onBlur={commitName}
            onSubmitEditing={commitName}
            autoFocus
            returnKeyType="done"
          />
        ) : (
          <Pressable onPress={() => { setNameDraft(domain.name); setEditingName(true); }} style={{ flex: 1 }}>
            <Text style={[styles.cardTitle, { color: domain.color }]}>{domain.name}</Text>
          </Pressable>
        )}
        <Pressable onPress={confirmRemove} hitSlop={8}>
          <Text style={styles.removeText}>Delete</Text>
        </Pressable>
      </View>

      <Text style={styles.statusLine}>
        {isAligned ? 'Aligned' : `Action: ${actionDeg}° · System: ${sysDeg}°`}
        {'  ·  Streak '}
        {state.streak}
        {state.missStreak > 0 ? `  ·  Miss streak ${state.missStreak}` : ''}
      </Text>

      {visionText ? <Text style={styles.fieldText}>Vision: {visionText}</Text> : null}
      {domain.systemText ? <Text style={styles.fieldText}>System: {domain.systemText}</Text> : null}
      {domain.taskText ? <Text style={styles.fieldText}>Task: {domain.taskText}</Text> : null}

      {habitNames.length > 0 ? (
        <Text style={styles.fieldText}>Habits: {habitNames.join(', ')}</Text>
      ) : null}
      {actionTexts.length > 0 ? (
        <View style={styles.bulletBlock}>
          <Text style={styles.fieldLabel}>Actions</Text>
          {actionTexts.map((t, i) => (
            <Text style={styles.bulletText} key={i}>
              • {t}
            </Text>
          ))}
        </View>
      ) : null}
      {principleTexts.length > 0 ? (
        <View style={styles.bulletBlock}>
          <Text style={styles.fieldLabel}>Principles</Text>
          {principleTexts.map((t, i) => (
            <Text style={styles.bulletText} key={i}>
              • {t}
            </Text>
          ))}
        </View>
      ) : null}

      <View style={styles.markRow}>
        <Pressable
          onPress={() => onMark('hit')}
          style={[styles.markBtn, { backgroundColor: state.pending === 'hit' ? domain.color : t.onPace }]}
        >
          <Text style={styles.markBtnText}>Did the work</Text>
        </Pressable>
        <Pressable
          onPress={() => onMark('miss')}
          style={[styles.markBtn, { backgroundColor: state.pending === 'miss' ? t.lapsed : t.glass }]}
        >
          <Text style={[styles.markBtnText, state.pending !== 'miss' && { color: c.inkSoft }]}>Missed it</Text>
        </Pressable>
      </View>

      <Pressable onPress={() => setExpanded((v) => !v)} style={styles.expandToggle}>
        <Text style={styles.expandToggleText}>{expanded ? 'Hide endeavour setup ▲' : 'Configure endeavour ▼'}</Text>
      </Pressable>

      {expanded ? (
        <View style={styles.cascadeBox}>
          <Text style={styles.fieldLabel}>Endeavour (WHY)</Text>
          <View style={styles.chipRow}>
            {(() => {
              const sector = domainIdToSector(domain.id);
              const endeavourSuggestions = sector ? PRESET_ENDEAVOURS[sector] : [];
              return (
                <>
                  {endeavourSuggestions.map((endeavour) => (
                    <Chip
                      key={endeavour}
                      label={endeavour}
                      selected={cascade.selectedVisionId === endeavour}
                      color={domain.color}
                      onPress={() => onCascadeChange({ selectedVisionId: endeavour, customVisionText: '' })}
                    />
                  ))}
                  <Chip
                    label="Custom…"
                    selected={cascade.selectedVisionId === '__custom__'}
                    color={domain.color}
                    onPress={() => onCascadeChange({ selectedVisionId: '__custom__' })}
                  />
                </>
              );
            })()}
          </View>
          {cascade.selectedVisionId === '__custom__' ? (
            <TextInput
              style={styles.textArea}
              placeholder="Type your endeavour..."
              placeholderTextColor={c.slate}
              value={customVisionDraft}
              onChangeText={setCustomVisionDraft}
              onBlur={() => onCascadeChange({ customVisionText: customVisionDraft })}
              multiline
            />
          ) : null}

          <Text style={styles.fieldLabel}>Habit (HOW)</Text>
          <View style={styles.chipRow}>
            {(preset?.habits || []).map((h) => (
              <Chip
                key={h.id}
                label={h.name}
                selected={(cascade.selectedHabitIds || []).includes(h.id)}
                color={domain.color}
                onPress={() => toggleHabit(h.id)}
              />
            ))}
          </View>
          {(cascade.customHabits || []).map((h) => (
            <View style={styles.customRow} key={h.id}>
              <Text style={styles.customRowText}>{h.name}</Text>
              <Pressable onPress={() => onCascadeChange({ customHabits: (cascade.customHabits || []).filter((ch) => ch.id !== h.id) })}>
                <Text style={styles.removeText}>Remove</Text>
              </Pressable>
            </View>
          ))}
          <AddInlineField
            placeholder="Custom habit..."
            onSubmit={(name) =>
              onCascadeChange({
                customHabits: [...(cascade.customHabits || []), { id: `custom-${Date.now()}`, name, tasks: [] }],
              })
            }
          />

          <Text style={styles.fieldLabel}>Actions (WHAT)</Text>
          {availableTasks.length === 0 ? (
            <Text style={styles.emptyInline}>Select habits to see available actions.</Text>
          ) : (
            <View style={styles.chipRow}>
              {availableTasks.map((t) => (
                <Chip
                  key={t.id}
                  label={t.text}
                  selected={(cascade.selectedTaskIds || []).includes(t.id)}
                  color={domain.color}
                  onPress={() => toggleTask(t.id)}
                />
              ))}
            </View>
          )}
          {(cascade.customTaskTexts || []).map((t, i) => (
            <View style={styles.customRow} key={`${t}-${i}`}>
              <Text style={styles.customRowText}>{t}</Text>
              <Pressable
                onPress={() =>
                  onCascadeChange({ customTaskTexts: (cascade.customTaskTexts || []).filter((_, idx) => idx !== i) })
                }
              >
                <Text style={styles.removeText}>Remove</Text>
              </Pressable>
            </View>
          ))}
          <AddInlineField
            placeholder="Custom action..."
            onSubmit={(text) => onCascadeChange({ customTaskTexts: [...(cascade.customTaskTexts || []), text] })}
          />

          <Text style={styles.fieldLabel}>Principles</Text>
          {availablePrinciples.length === 0 ? (
            <Text style={styles.emptyInline}>No principles available.</Text>
          ) : (
            <View style={styles.chipRow}>
              {availablePrinciples.map((p) => (
                <Chip
                  key={p.id}
                  label={p.text}
                  selected={(cascade.selectedPrincipleIds || []).includes(p.id)}
                  color={domain.color}
                  onPress={() => togglePrinciple(p.id)}
                />
              ))}
            </View>
          )}
          {(cascade.customPrinciples || []).map((p, i) => (
            <View style={styles.customRow} key={`${p}-${i}`}>
              <Text style={styles.customRowText}>{p}</Text>
              <Pressable
                onPress={() =>
                  onCascadeChange({ customPrinciples: (cascade.customPrinciples || []).filter((_, idx) => idx !== i) })
                }
              >
                <Text style={styles.removeText}>Remove</Text>
              </Pressable>
            </View>
          ))}
          <AddInlineField
            placeholder="Custom principle..."
            onSubmit={(text) => onCascadeChange({ customPrinciples: [...(cascade.customPrinciples || []), text] })}
          />

          <Text style={styles.fieldLabel}>Notes</Text>
          <TextInput
            style={styles.textArea}
            placeholder="Domain notes..."
            placeholderTextColor={c.slate}
            value={notesDraft}
            onChangeText={setNotesDraft}
            onBlur={() => onCascadeChange({ notes: notesDraft })}
            multiline
          />
        </View>
      ) : null}
    </GlassCard>
  );
}

// ---------------------------------------------------------------------------
// Main tool
// ---------------------------------------------------------------------------
interface TelosStateRow {
  cycle: number;
  domains: Record<string, TelosDomainState>;
  updated_at?: string;
}

export function TelosTool(_props: ToolProps) {
  const styles = useStyles();
  const { t, c } = useTheme();
  const { session } = useAuth();
  const userId = session?.user.id;
  const { width } = useWindowDimensions();
  const compassSize = Math.max(220, Math.min(width - 56, 300));

  const [telosSettings, setTelosSettings] = useState<TelosSettings | null>(null);
  const [cycle, setCycle] = useState(0);
  const [domainStates, setDomainStates] = useState<Record<string, TelosDomainState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stateSaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sweep line + lyre playback (mirrors telos/page.tsx's handlePlayLyre).
  const [sweepAngle, setSweepAngle] = useState<number | null>(null);
  const [isPlayingLyre, setIsPlayingLyre] = useState(false);
  const sweepRafRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (sweepRafRef.current) cancelAnimationFrame(sweepRafRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const [settingsRes, stateRes, spine] = await Promise.all([
      supabase.from('user_settings').select('telos_settings').eq('user_id', userId).maybeSingle(),
      supabase.from('telos_state').select('*').eq('user_id', userId).maybeSingle(),
      loadOracleSpine(supabase, userId).catch((): OracleSpine | null => null),
    ]);

    if (settingsRes.error) {
      setError(settingsRes.error.message);
      setLoading(false);
      return;
    }
    if (stateRes.error) {
      setError(stateRes.error.message);
      setLoading(false);
      return;
    }

    const loadedTelos = (settingsRes.data?.telos_settings as Partial<TelosSettings> | null | undefined) || null;
    const nextTelosSettings: TelosSettings = {
      ...DEFAULT_TELOS_SETTINGS,
      ...(loadedTelos || {}),
      domains: loadedTelos?.domains && loadedTelos.domains.length > 0 ? loadedTelos.domains : DEFAULT_TELOS_SETTINGS.domains,
    };

    // The ORACLE spine is the source of truth for each sector's Endeavour;
    // TELOS's own text is only the fallback when the spine has none.
    if (spine) {
      nextTelosSettings.domains = nextTelosSettings.domains.map((d) => {
        const sector = domainIdToSector(d.id);
        const endeavour = sector ? spine[sector]?.endeavour?.trim() : '';
        if (!sector || !endeavour) return d;
        const isPreset = (PRESET_ENDEAVOURS[sector] ?? []).includes(endeavour);
        return {
          ...d,
          cascade: {
            ...(d.cascade || createDefaultCascade()),
            selectedVisionId: isPreset ? endeavour : '__custom__',
            customVisionText: isPreset ? '' : endeavour,
          },
        };
      });
    }

    const stateRow = stateRes.data as TelosStateRow | null;

    setTelosSettings(nextTelosSettings);
    setCycle(stateRow?.cycle || 0);
    setDomainStates(stateRow?.domains || {});
    setError(null);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeDomains = (telosSettings?.domains || []).filter((d) => d.isActive);

  // Ensure every active domain has state, same as web's effect.
  useEffect(() => {
    if (loading || !telosSettings) return;
    setDomainStates((prev) => {
      const next = { ...prev };
      let changed = false;
      activeDomains.forEach((d) => {
        if (!next[d.id]) {
          next[d.id] = createDefaultDomainState();
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telosSettings, loading]);

  const saveTelosState = useCallback(
    (newCycle: number, newStates: Record<string, TelosDomainState>) => {
      if (!userId) return;
      if (stateSaveTimeout.current) clearTimeout(stateSaveTimeout.current);
      stateSaveTimeout.current = setTimeout(async () => {
        const { error: err } = await supabase.from('telos_state').upsert({
          user_id: userId,
          cycle: newCycle,
          domains: newStates,
          updated_at: new Date().toISOString(),
        });
        if (err) setError(err.message);
      }, 800);
    },
    [userId]
  );

  useEffect(() => {
    return () => {
      if (stateSaveTimeout.current) clearTimeout(stateSaveTimeout.current);
    };
  }, []);

  const saveTelosSettings = useCallback(
    async (next: TelosSettings) => {
      if (!userId) return;
      const { error: err } = await supabase
        .from('user_settings')
        .upsert({ user_id: userId, telos_settings: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
      if (err) setError(err.message);
    },
    [userId]
  );

  const updateTelosSettings = useCallback(
    (next: TelosSettings) => {
      setTelosSettings(next);
      void saveTelosSettings(next);
    },
    [saveTelosSettings]
  );

  const handleCascadeChange = useCallback(
    (domainId: string, update: Partial<TelosDomainCascade>) => {
      if (!telosSettings) return;
      const nextDomains = telosSettings.domains.map((d) =>
        d.id === domainId ? { ...d, cascade: { ...(d.cascade || createDefaultCascade()), ...update } } : d
      );
      updateTelosSettings({ ...telosSettings, domains: nextDomains });

      // Endeavour edits also go to the ORACLE spine (re-read first so a stale
      // local copy never clobbers other sectors/aims).
      if (userId && ('selectedVisionId' in update || 'customVisionText' in update)) {
        const edited = nextDomains.find((d) => d.id === domainId);
        const sector = edited ? domainIdToSector(edited.id) : null;
        const endeavour = edited ? resolveVisionText(edited).trim() : '';
        if (sector && endeavour) {
          void (async () => {
            try {
              const spine = await loadOracleSpine(supabase, userId);
              if (spine[sector].endeavour === endeavour) return;
              await saveOracleSpine(supabase, userId, {
                ...spine,
                [sector]: { ...spine[sector], endeavour, endeavourSource: 'manual', isDirty: true },
              });
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not save Endeavour to ORACLE');
            }
          })();
        }
      }
    },
    [telosSettings, updateTelosSettings, userId]
  );

  const renameDomain = useCallback(
    (domainId: string, name: string) => {
      if (!telosSettings) return;
      updateTelosSettings({
        ...telosSettings,
        domains: telosSettings.domains.map((d) => (d.id === domainId ? { ...d, name } : d)),
      });
    },
    [telosSettings, updateTelosSettings]
  );

  const removeDomain = useCallback(
    (domainId: string) => {
      if (!telosSettings) return;
      updateTelosSettings({
        ...telosSettings,
        domains: telosSettings.domains.filter((d) => d.id !== domainId),
      });
    },
    [telosSettings, updateTelosSettings]
  );

  // Deity form-fill adapter (docs/DEITY_AGENT_BACKEND_DESIGN.md §4/§7).
  // recordId is the target domain's id: fields merge onto that domain object.
  // Without a recordId, fields are treated as a settings-level patch (e.g. a
  // full replacement `domains` array). Applying always routes through
  // updateTelosSettings, the same upsert-to-user_settings path every manual
  // domain edit uses.
  useEffect(() => {
    registerDeityForm('telos_domain', {
      getCurrentValues: () => (telosSettings ? { domains: telosSettings.domains } : undefined),
      applyFill: (fields, recordId) => {
        if (!telosSettings) return;
        if (recordId) {
          updateTelosSettings({
            ...telosSettings,
            domains: telosSettings.domains.map((d) => (d.id === recordId ? { ...d, ...fields } : d)),
          });
        } else {
          updateTelosSettings({ ...telosSettings, ...(fields as Partial<TelosSettings>) });
        }
      },
    });
    return () => unregisterDeityForm('telos_domain');
  }, [telosSettings, updateTelosSettings]);

  const addDomain = useCallback(() => {
    if (!telosSettings) return;
    const newDomain: TelosDomainConfig = {
      id: `telos-${Date.now()}`,
      name: 'New Domain',
      color: c.inkSoft,
      isActive: true,
      visionText: '',
      systemText: '',
      taskText: '',
      chord: ['C4', 'E4', 'G4'],
      cascade: createDefaultCascade(),
    };
    updateTelosSettings({ ...telosSettings, domains: [...telosSettings.domains, newDomain] });
  }, [telosSettings, updateTelosSettings]);

  const markDomain = useCallback((domainId: string, action: 'hit' | 'miss') => {
    setDomainStates((prev) => ({
      ...prev,
      [domainId]: { ...(prev[domainId] || createDefaultDomainState()), pending: action },
    }));
  }, []);

  const advanceCycle = useCallback(() => {
    const newCycle = cycle + 1;
    setCycle(newCycle);
    setDomainStates((prev) => {
      const next = advanceCycleState(activeDomains, prev);
      saveTelosState(newCycle, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, activeDomains, saveTelosState]);

  const resetAll = useCallback(() => {
    const newStates: Record<string, TelosDomainState> = {};
    activeDomains.forEach((d) => {
      newStates[d.id] = createDefaultDomainState();
    });
    setCycle(0);
    setDomainStates(newStates);
    saveTelosState(0, newStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDomains, saveTelosState]);

  const simulateDrift = useCallback(() => {
    setDomainStates((prev) => {
      const next = { ...prev };
      activeDomains.forEach((d, i) => {
        const drifts = [0, -0.22, 0, -0.12, -0.3];
        next[d.id] = {
          ...(next[d.id] || createDefaultDomainState()),
          actionOffset: drifts[i % drifts.length],
          systemOffset: drifts[i % drifts.length] * 0.5,
        };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDomains]);

  const handlePlayLyre = useCallback(() => {
    if (isPlayingLyre || !telosSettings?.lyreEnabled || activeDomains.length === 0) return;
    setIsPlayingLyre(true);

    const totalDuration = 2.2; // seconds, matches web's lyre.ts
    const sweepStart = -Math.PI / 2;
    const n = activeDomains.length;

    activeDomains.forEach((domain, i) => {
      const baseAngle = sweepStart + (2 * Math.PI / n) * i;
      let norm = baseAngle - sweepStart;
      while (norm < 0) norm += Math.PI * 2;
      while (norm >= Math.PI * 2) norm -= Math.PI * 2;
      const delayMs = (norm / (Math.PI * 2)) * totalDuration * 1000;
      const drift = domainStates[domain.id]?.actionOffset || 0;
      setTimeout(() => {
        playChord(domain.chord, drift).catch((err) => console.error('Lyre chord error:', err));
      }, delayMs);
    });

    const startMs = Date.now();
    const animate = () => {
      const elapsed = (Date.now() - startMs) / 1000;
      const progress = Math.min(elapsed / totalDuration, 1);
      setSweepAngle(sweepStart + progress * Math.PI * 2);

      if (progress < 1) {
        sweepRafRef.current = requestAnimationFrame(animate);
      } else {
        setSweepAngle(null);
        setIsPlayingLyre(false);
      }
    };

    if (sweepRafRef.current) cancelAnimationFrame(sweepRafRef.current);
    sweepRafRef.current = requestAnimationFrame(animate);
  }, [isPlayingLyre, telosSettings?.lyreEnabled, activeDomains, domainStates]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.error}>{error}</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
<PageBg />
<ScrollView style={styles.root} contentContainerStyle={styles.content}>
      <View>
        <Text style={styles.title}>T.E.L.O.S</Text>
        <Text style={styles.subtitle}>
          Life is progress or degradation. The rings make entropy visible before it becomes a crisis.
        </Text>
      </View>

      <View style={styles.legendRow}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: t.ivory }]} />
          <Text style={styles.legendText}>Actions (inner)</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: t.ivorySoft }]} />
          <Text style={styles.legendText}>Systems (middle)</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: t.ivoryFaint }]} />
          <Text style={styles.legendText}>Vision (outer, fixed)</Text>
        </View>
      </View>

      <GlassCard style={styles.compassWrap}>
        {activeDomains.length > 0 ? (
          <TelosCompass
            domains={telosSettings?.domains || []}
            domainStates={domainStates}
            size={compassSize}
            sweepAngle={sweepAngle}
          />
        ) : (
          <Text style={styles.emptyInline}>No domains configured. Add one to get started.</Text>
        )}
      </GlassCard>

      <View style={styles.controlsRow}>
        <Pressable
          style={[styles.controlBtn, (isPlayingLyre || !telosSettings?.lyreEnabled) && styles.controlBtnDisabled]}
          onPress={handlePlayLyre}
          disabled={isPlayingLyre || !telosSettings?.lyreEnabled}
        >
          <Text style={styles.controlBtnText}>Play Lyre</Text>
        </Pressable>
        <Pressable style={styles.controlBtn} onPress={resetAll}>
          <Text style={styles.controlBtnText}>Reset</Text>
        </Pressable>
        <Pressable style={styles.controlBtn} onPress={simulateDrift}>
          <Text style={styles.controlBtnText}>Simulate Drift</Text>
        </Pressable>
      </View>

      <View style={styles.cycleRow}>
        <Text style={styles.cycleText}>Cycle: {cycle}</Text>
        <Pressable style={styles.nextCycleBtn} onPress={advanceCycle}>
          <Text style={styles.nextCycleText}>Next Cycle ›</Text>
        </Pressable>
      </View>

      {activeDomains.map((d) => (
        <DomainCard
          key={d.id}
          domain={d}
          state={domainStates[d.id] || createDefaultDomainState()}
          onMark={(action) => markDomain(d.id, action)}
          onRename={(name) => renameDomain(d.id, name)}
          onRemove={() => removeDomain(d.id)}
          onCascadeChange={(update) => handleCascadeChange(d.id, update)}
        />
      ))}

      <Pressable style={styles.addDomainBtn} onPress={addDomain}>
        <Text style={styles.addDomainText}>+ Add Domain</Text>
      </Pressable>

      <Text style={styles.footerNote}>
        <Text style={styles.footerNoteStrong}>Vision never moves.</Text> Your actions are the only thing you
        control. Do the work and your action node stays on the spoke. Miss it and it drifts. Miss enough and the
        system gets pulled off too.
      </Text>
    </ScrollView>
</View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  content: { padding: 14, gap: 14, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 8 },
  error: { color: c.danger, fontSize: 13, textAlign: 'center' },

  title: { fontSize: 20, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  subtitle: { fontSize: 12, color: c.slate, marginTop: 2, lineHeight: 17 },

  legendRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 9, height: 9, borderRadius: 5 },
  legendText: { fontSize: 11, color: c.slate },

  compassWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderRadius: radii.panel,
    paddingVertical: 16,
    minHeight: 120,
    ...neu.raised,
  },

  controlsRow: { flexDirection: 'row', gap: 10, justifyContent: 'center' },
  controlBtn: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  controlBtnText: { color: c.blueText, fontWeight: '700', fontSize: 13 },
  controlBtnDisabled: { opacity: 0.45 },

  cycleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12 },
  cycleText: { fontSize: 14, fontWeight: '800', color: c.text },
  nextCycleBtn: { backgroundColor: c.lime, borderRadius: radii.tile, paddingHorizontal: 16, paddingVertical: 9, ...neu.glowPrimary },
  nextCycleText: { fontWeight: '900', color: c.blueText, fontSize: 13 },

  card: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 14,
    gap: 6,
    ...neu.raised,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardTitle: { fontSize: 16, fontWeight: '900', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  nameInput: {
    fontSize: 16,
    fontWeight: '900',
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    ...neu.insetDeep,
  },
  removeText: { color: c.danger, fontSize: 12, fontWeight: '700' },

  statusLine: { fontSize: 11, color: c.slate },
  fieldText: { fontSize: 13, color: c.text, marginTop: 2 },
  fieldLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, fontWeight: '800', color: c.slate, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 8 },
  bulletBlock: { marginTop: 2, gap: 2 },
  bulletText: { fontSize: 13, color: c.text, lineHeight: 18 },
  emptyInline: { fontSize: 13, color: c.slate, fontStyle: 'italic', textAlign: 'center' },

  markRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  markBtn: { flex: 1, borderRadius: radii.tile, paddingVertical: 9, alignItems: 'center' },
  markBtnText: { fontSize: 12, fontWeight: '700', color: t.onGold },

  expandToggle: { marginTop: 8, alignItems: 'center' },
  expandToggleText: { fontSize: 12, fontWeight: '700', color: c.blueText },

  cascadeBox: { marginTop: 4, gap: 4 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  chip: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: 220,
    ...neu.raisedSm,
  },
  chipText: { fontSize: 11, color: c.text },
  chipTextSelected: { color: t.onGold, fontWeight: '700' },

  customRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
    paddingVertical: 2,
  },
  customRowText: { flex: 1, fontSize: 12, fontStyle: 'italic', color: c.text },

  addRow: { flexDirection: 'row', gap: 6, marginTop: 6 },
  addInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 12,
    color: c.text,
    ...neu.insetDeep,
  },
  addBtn: {
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    justifyContent: 'center',
    ...neu.glowPrimary,
  },
  addBtnText: { fontWeight: '900', color: c.blueText },

  textArea: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 12,
    color: c.text,
    marginTop: 4,
    minHeight: 60,
    textAlignVertical: 'top',
    ...neu.insetDeep,
  },

  addDomainBtn: {
    alignSelf: 'center',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 16,
    paddingVertical: 9,
    ...neu.raisedSm,
  },
  addDomainText: { color: c.blueText, fontWeight: '800', fontSize: 13 },

  footerNote: { fontSize: 11, color: c.slate, textAlign: 'center', lineHeight: 16, marginTop: 4 },
  footerNoteStrong: { color: c.text, fontWeight: '800' },
}),
);
